import React from 'react';
import ProductPreview from '@gik/product-page/components/ProductPreview/ProductPreview';
import ProductDetails from '@gik/product-page/components/ProductDetails/ProductDetails';
import ProductDescription from '@gik/product-page/components/ProductDescription/ProductDescription';
import { LoadingSpinner, ParagraphBone, FrameBone, ButtonBone, WordBone } from '@gik/ui';
import { useProducts } from '@gik/checkout/api/products';
import { CartItem, createCartItemFromProduct } from '@gik/checkout';
import { InkindPageViewHistoryItem } from '@gik/inkind-page';
import ProductActions from '@gik/product-page/components/ProductActions/ProductActions';
import { Product } from '@gik/core/models/gik/Product';
import bemBlock from '@gik/core/utils/bemBlock';
import withSuspense from '@gik/core/components/suspended/withSuspense';

export const productSectionBlockName = 'product-section';
export interface IProductSectionContext {
  product: Product;
  setProduct?: React.Dispatch<React.SetStateAction<Product>>;

  selectedCartItem?: CartItem;
  setSelectedCartItem?: React.Dispatch<React.SetStateAction<CartItem>>;

  inkindRouteId?: string;
  inkindPageViewHistory: InkindPageViewHistoryItem[];

  type?: 'wishlist' | 'productPage';

  hasErrors?: boolean;
  setHasErrors?: React.Dispatch<React.SetStateAction<boolean>>;

  showSkeleton?: boolean;
  showCustomError?: boolean;
  setShowCustomError?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProductSectionContext = React.createContext<IProductSectionContext>(null);

export interface IProductSectionProps {
  productId: number;
  inkindRouteId?: string;
  inkindPageViewHistory: InkindPageViewHistoryItem[];
  type: 'wishlist' | 'productPage';
  skeleton?: boolean;
}

const skelBem = bemBlock(`product-section-skeleton`);
const Skeleton = (): React.ReactElement => (
  <div className={`${skelBem()}`}>
    <aside>
      <FrameBone className={skelBem('image')} />
      <header>
        <ParagraphBone words={5} className={skelBem('title')} />
        <WordBone width={80} className={skelBem('price')} />
      </header>
    </aside>
    <main>
      <header>
        <ParagraphBone words={5} className={skelBem('title')} />
        <WordBone width={80} className={skelBem('price')} />
      </header>
      <div className={skelBem('actions')}>
        <ButtonBone />
        <ButtonBone />
        <ButtonBone />
      </div>
      <ParagraphBone words={40} className={skelBem('text')} />
      <br />
      <ParagraphBone words={80} className={skelBem('text')} />
      <br />
      <ParagraphBone words={40} className={skelBem('text')} />
    </main>
  </div>
);

function ProductSection({
  productId,
  inkindRouteId,
  inkindPageViewHistory,
  type,
  skeleton,
}: IProductSectionProps): JSX.Element {
  const { data: products, error: apiError } = useProducts([productId]);

  const [error, setError] = React.useState<boolean>(false);
  const [product, setProduct] = React.useState<Product>(null);
  const [selectedCartItem, setSelectedCartItem] = React.useState<CartItem>(null);
  const [hasErrors, setHasErrors] = React.useState<boolean>(false);
  const [showCustomError, setShowCustomError] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (products && !product) {
      if (products.length) {
        const firstProduct = products[0];
        setProduct(firstProduct);

        if ((firstProduct.variations?.length || 0) <= 1) {
          setSelectedCartItem(createCartItemFromProduct(firstProduct));
        }
      } else {
        setError(true);
      }
    }
  }, [products, product]);

  React.useEffect(() => {
    if (apiError) {
      console.error(apiError);
      setError(true);
    }
  }, [apiError]);

  const bem = bemBlock(productSectionBlockName);

  const isLoading = !product && !error;
  const isError = isLoading && apiError;

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  if (error) return <div>There was an error retrieving this product.</div>;

  return (
    // TODO: remove ProductSectionContext as it makes product actions difficult to use outside of ProductSection
    <ProductSectionContext.Provider
      value={{
        product,
        setProduct,
        selectedCartItem,
        setSelectedCartItem,
        inkindRouteId,
        inkindPageViewHistory,
        type,
        hasErrors,
        setHasErrors,
        showCustomError,
        setShowCustomError,
        showSkeleton: skeleton,
      }}
    >
      <div className={bem()}>
        <section>
          <ProductPreview />
          {/* the component below is only rendered on breakpoint md */}
          <ProductDetails />
        </section>
        <section>
          <ProductDetails />
          <ProductActions />
          <ProductDescription />
        </section>
      </div>
    </ProductSectionContext.Provider>
  );
}

export default withSuspense(ProductSection, Skeleton);
